import { Controller } from "@hotwired/stimulus"

// Modal controller for URL-addressable frame modals that keeps the browser
// URL/history in sync so back/forward and refresh work — WITHOUT Turbo
// `advance`.
//
// Turbo `advance` strands history on a fragment-only `…/modal` URL that can't be
// restored (it redirects to a full page, dumping you out of the modal). Instead
// we push REAL page URLs: `<current page>?modal_attachment=<id>` (or the task /
// contact equivalent), which the layout's modal frame reopens as an overlay on
// restore/refresh/direct-visit.
// Because every history entry is a real, renderable page, restoration can never
// dead-end.
//
// Unlike the stock tailwindcss-stimulus-components Modal, this controller does
// NOT close the dialog on `turbo:before-cache`. That close fires a `close` event
// on every Turbo navigation, which would corrupt our history sync; on restore we
// reopen via connect() instead.
export default class extends Controller {
  static targets = ["dialog"]
  static values = {
    open: Boolean,
    attachmentId: String,
    type: { type: String, default: "attachment" },
    id: String,
    src: String
  }

  connect() {
    this.closeStarted = false
    if (this.openSuppressedByRouter()) {
      this.element.remove()
      return
    }

    if (this.openValue) this.reopenAsModal()

    // Push a real page URL when the modal was opened fresh. When it's already
    // in the URL we arrived via a restore/refresh, so don't push a duplicate
    // entry.
    this.stackDepth = this.currentStackDepth

    if (this.modalId) {
      const current = new URLSearchParams(window.location.search).get(this.modalParamName)
      if (current !== this.modalId) {
        const url = new URL(window.location.href)
        this.clearModalParams(url)
        url.searchParams.set(this.modalParamName, this.modalId)

        this.stackDepth = this.currentStackDepth + 1
        window.history.pushState(
          {
            ...window.history.state,
            modal: {
              type: this.typeValue,
              id: this.modalId,
              src: this.modalSrc
            },
            modalStackDepth: this.stackDepth
          },
          "",
          url.toString()
        )
      }
    }

    this.boundOnClose = this.onDialogClose.bind(this)
    this.boundOnCancel = this.onDialogCancel.bind(this)
    this.dialogTarget.addEventListener("close", this.boundOnClose)
    this.dialogTarget.addEventListener("cancel", this.boundOnCancel)
  }

  disconnect() {
    this.dialogTarget.removeEventListener("close", this.boundOnClose)
    this.dialogTarget.removeEventListener("cancel", this.boundOnCancel)
  }

  // showModal() throws if the dialog already has the `open` attribute (e.g.
  // restored from a Turbo snapshot), so reset first, then show as a true modal.
  reopenAsModal() {
    if (this.dialogTarget.open) this.dialogTarget.close()
    this.dialogTarget.showModal()
  }

  open() {
    this.dialogTarget.showModal()
  }

  // Allows for a closing animation since display transitions don't work yet.
  close(event) {
    event?.preventDefault?.()

    if (this.closeStarted || !this.dialogTarget.open) return

    this.closeStarted = true
    this.dialogTarget.setAttribute("closing", "")

    Promise.all(
      this.dialogTarget.getAnimations().map((animation) => animation.finished),
    ).then(() => {
      this.dialogTarget.removeAttribute("closing")
      this.dialogTarget.close()
    })
  }

  backdropClose(event) {
    if (event.target.nodeName === "DIALOG") this.close()
  }

  onDialogCancel(event) {
    event.preventDefault()
    this.close()
  }

  // Sync history when the user dismisses the modal (Esc, backdrop, or Close).
  // Escape/Close should close the modal stack, not behave like the browser Back
  // button. Browser Back/Forward remain owned by modal-router's popstate sync.
  onDialogClose() {
    if (!this.modalId) return

    const current = new URLSearchParams(window.location.search).get(this.modalParamName)
    if (current !== this.modalId) return // history already moved on

    window.dispatchEvent(
      new CustomEvent("modal-router:close-stack", {
        detail: {
          depth: this.stackDepth,
          type: this.typeValue,
          id: this.modalId
        }
      })
    )
  }

  get modalId() {
    return this.hasIdValue ? this.idValue : this.attachmentIdValue
  }

  get modalSrc() {
    return this.hasSrcValue ? this.srcValue : window.location.pathname + window.location.search
  }

  get currentStackDepth() {
    return Number(window.history.state?.modalStackDepth || 0)
  }

  get modalParamName() {
    return {
      attachment: "modal_attachment",
      task: "modal_task",
      contact: "modal_contact"
    }[this.typeValue] || `modal_${this.typeValue}`
  }

  clearModalParams(url) {
    for (const param of ["modal_attachment", "modal_task", "modal_contact"]) {
      url.searchParams.delete(param)
    }
  }

  openSuppressedByRouter() {
    if (!this.modalId) return false

    const event = new CustomEvent("modal-router:before-open", {
      cancelable: true,
      detail: {
        type: this.typeValue,
        id: this.modalId
      }
    })
    window.dispatchEvent(event)

    return event.defaultPrevented
  }
}
